import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useMainPageTitle } from "../../contexts/PageContext";
import api from "../../services/api";
import ProfileImage from "../../components/profileImage";

const CreateCompetitionRequest = () => {
  const { updatePageTitle } = useMainPageTitle();
  const navigate = useNavigate();

  // Multi-step form state
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 3;

  // Step 1: Team Search
  const [uniqueName, setUniqueName] = useState("");
  const [searchedTeam, setSearchedTeam] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState("");

  // Step 2: Competition Details
  const [formData, setFormData] = useState({
    senderTeamId: '',
    title: '',
    description: '',
    message: ''
  });

  // Step 3: Schedule Details
  const [scheduleData, setScheduleData] = useState({
    proposedStartDate: ''
  });

  const [teams, setTeams] = useState([]);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Team selection dropdown state
  const [openDropdown, setOpenDropdown] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    updatePageTitle("Create Competition Request", "Create a new competition request");
    fetchTeams();
    
    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [updatePageTitle]);

  const fetchTeams = async () => {
    try {
      const response = await api.get('/team/get-all-my-organization-teams');
      setTeams(response.data.teams || []);
    } catch (err) {
      console.error('Error fetching teams:', err);
    }
  };

  // Step 1: Team Search Functions
  const handleTeamSearch = async () => {
    if (!uniqueName.trim()) {
      setSearchError("Please enter a team unique name");
      return;
    }

    setSearchLoading(true);
    setSearchedTeam(null);
    setSearchError("");

    try {
      const response = await api.get(`/team/search-team-by-team-unique-name?uniqueName=${encodeURIComponent(uniqueName.trim())}`);

      if (response.data.team) {
        setSearchedTeam(response.data.team);
      } else {
        setSearchError(response.data.message || "Team not found");
      }
    } catch (err) {
      console.error('Error searching for team:', err);
      setSearchError("Error searching for team");
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSelectTeam = useCallback(() => {
    if (searchedTeam) {
      setCurrentStep(2);
    }
  }, [searchedTeam]);

  // Form handlers - memoized to prevent unnecessary re-renders
  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  }, [errors]);

  const handleScheduleChange = useCallback((e) => {
    const { name, value } = e.target;
    setScheduleData(prev => ({ ...prev, [name]: value }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  }, [errors]);

  // Step navigation
  const handleNextStep = () => {
    if (currentStep === 2) {
      if (validateCompetitionDetails()) {
        setCurrentStep(3);
      }
    }
  };

  const handlePrevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Validation functions
  const validateCompetitionDetails = () => {
    const newErrors = {};

    if (!formData.senderTeamId) newErrors.senderTeamId = 'Please select your team';
    if (!formData.title.trim()) newErrors.title = 'Title is required';
    if (formData.title.length < 3) newErrors.title = 'Title must be at least 3 characters';
    if (formData.title.length > 100) newErrors.title = 'Title must not exceed 100 characters';
    if (!formData.description.trim()) newErrors.description = 'Description is required';
    if (formData.description.length < 10) newErrors.description = 'Description must be at least 10 characters';
    if (formData.description.length > 1000) newErrors.description = 'Description must not exceed 1000 characters';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateScheduleDetails = () => {
    const newErrors = {};

    if (!scheduleData.proposedStartDate) newErrors.proposedStartDate = 'Start date is required';

    // Validate dates
    const now = new Date();
    const startDate = new Date(scheduleData.proposedStartDate);

    if (startDate <= now) {
      newErrors.proposedStartDate = 'Start date must be in the future';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Submit function
  const handleSubmit = async () => {
    if (!validateScheduleDetails()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const requestData = {
        senderTeamId: formData.senderTeamId,
        receiverTeamId: searchedTeam._id,
        receiverOrganizationId: searchedTeam.organization._id,
        title: formData.title,
        description: formData.description,
        message: formData.message,
        proposedMatchDate: scheduleData.proposedStartDate,
        proposedStartDate: scheduleData.proposedStartDate
      };

      await api.post('/competition-requests/create', requestData);
      
      // Navigate back to competition requests list
      navigate('/organization/competition-requests', {
        state: { 
          message: 'Competition request created successfully!',
          type: 'success'
        }
      });
    } catch (err) {
      console.error('Error creating competition request:', err);
      
      if (err.response?.data?.error) {
        setErrors({ submit: err.response.data.message || err.response.data.error });
      } else {
        setErrors({ submit: 'An error occurred while creating the competition request' });
      }
    } finally {
      setIsSubmitting(false);
    }
  };



  // Helper functions for team selection - memoized to prevent unnecessary re-renders
  const getSelectedTeam = useCallback((teamId) => {
    return teams.find(team => team._id === teamId);
  }, [teams]);

  // Memoized selected team object to prevent ProfileImage re-renders
  const selectedSenderTeam = useMemo(() => {
    return formData.senderTeamId ? getSelectedTeam(formData.senderTeamId) : null;
  }, [formData.senderTeamId, getSelectedTeam]);

  // Memoized searched team card to prevent ProfileImage refresh on input changes
  const SearchedTeamCard = useMemo(() => {
    if (!searchedTeam) return null;

    return (
      <div className="bg-gradient-to-br from-gray-50 to-white border-2 border-dashed border-gray-200 rounded-xl p-6 hover:border-blue-300 transition-all duration-300">
        <div className="text-center">
          <div className="relative inline-block mb-4">
            <ProfileImage
              className="w-20 h-20 rounded-full overflow-hidden ring-4 ring-white shadow-lg hover:ring-blue-200 transition-all duration-300"
              alt={searchedTeam.name}
              image={searchedTeam.logo}
            />
            <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-green-500 rounded-full border-2 border-white flex items-center justify-center">
              <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            </div>
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">
            {searchedTeam.name}
          </h3>
          <p className="text-lg text-gray-500 mb-4">@{searchedTeam.uniqueName}</p>
          {searchedTeam.sport && (
            <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800 mb-6">
              <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10 12a2 2 0 100-4 2 2 0 000 4z"/>
                <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd"/>
              </svg>
              {searchedTeam.sport}
            </div>
          )}
          
          <button
            onClick={handleSelectTeam}
            className="w-full inline-flex justify-center items-center px-6 py-4 border border-transparent text-base font-medium rounded-lg shadow-sm text-white bg-blue-600 hover:bg-blue-700 transform hover:scale-105 transition-all duration-200"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Select This Team
          </button>
        </div>
      </div>
    );
  }, [searchedTeam, handleSelectTeam]);

  // Memoized opponent team display for step 2 to prevent ProfileImage refresh
  const OpponentTeamDisplay = useMemo(() => {
    if (!searchedTeam) return null;

    return (
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="text-sm font-medium text-blue-800 mb-2">Selected Opponent</h3>
        <div className="flex items-center space-x-3">
          <ProfileImage
            className="w-12 h-12 rounded-full"
            alt={searchedTeam.name}
            image={searchedTeam.logo}
          />
          <div>
            <p className="font-semibold text-blue-900">{searchedTeam.name}</p>
            <p className="text-sm text-blue-700">@{searchedTeam.uniqueName}</p>
          </div>
        </div>
      </div>
    );
  }, [searchedTeam]);

  // Memoized competition summary for step 3 to prevent ProfileImage refresh
  const CompetitionSummary = useMemo(() => {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h3 className="text-sm font-medium text-gray-800 mb-3">Competition Summary</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div>
            <p className="text-gray-600 mb-2">Your Team:</p>
            {selectedSenderTeam && (
              <div className="flex items-center space-x-2">
                <ProfileImage
                  image={selectedSenderTeam.logo}
                  alt={selectedSenderTeam.name}
                  className="w-8 h-8 rounded-full object-cover"
                />
                <p className="font-semibold">{selectedSenderTeam.name}</p>
              </div>
            )}
          </div>
          <div>
            <p className="text-gray-600 mb-2">Opponent:</p>
            {searchedTeam && (
              <div className="flex items-center space-x-2">
                <ProfileImage
                  image={searchedTeam.logo}
                  alt={searchedTeam.name}
                  className="w-8 h-8 rounded-full object-cover"
                />
                <p className="font-semibold">{searchedTeam.name}</p>
              </div>
            )}
          </div>
          <div>
            <p className="text-gray-600">Competition:</p>
            <p className="font-semibold">{formData.title}</p>
          </div>
        </div>
      </div>
    );
  }, [selectedSenderTeam, searchedTeam, formData.title]);

  const handleTeamChange = useCallback((name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    setOpenDropdown(null);
    
    // Clear error when team is selected
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  }, [errors]);

  const TeamPreview = useMemo(() => {
    return ({ teamId }) => {
      const team = getSelectedTeam(teamId);
      if (!team) return null;

      return (
        <div className="mt-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <div className="flex items-center space-x-4">
            <div className="w-16 h-16 flex-shrink-0">
              <ProfileImage
                image={team.logo}
                alt={team.name}
                className="w-full h-full object-cover rounded-full border-2 border-gray-200"
              />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900">{team.name}</h3>
              <div className="text-sm text-gray-600 space-y-1">
                <p>Unique Name: {team.uniqueName}</p>
                <p>Type: {team.type}</p>
                <p>Sport: {team.sport}</p>
                <p>Gender: {team.gender}</p>
                <p>Members: {team.members?.length || 0}</p>
              </div>
            </div>
          </div>
        </div>
      );
    };
  }, [getSelectedTeam]);

  const CustomTeamDropdown = useMemo(() => {
    return ({ name, value, teams, label }) => {
      const selectedTeam = getSelectedTeam(value);

      return (
        <div className="relative" ref={dropdownRef}>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {label} <span className="text-red-500">*</span>
          </label>
          <div
            onClick={() => setOpenDropdown(openDropdown === name ? null : name)}
            className={`w-full px-3 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer bg-white ${
              errors[name] ? 'border-red-500' : 'border-gray-300'
            }`}
          >
            {selectedTeam ? (
              <div className="flex items-center space-x-3">
                <ProfileImage
                  image={selectedTeam.logo}
                  alt={selectedTeam.name}
                  className="w-8 h-8 rounded-full object-cover"
                />
                <div className="flex-1">
                  <span className="font-medium">{selectedTeam.name}</span>
                  <span className="text-sm text-gray-500 ml-2">({selectedTeam.uniqueName})</span>
                </div>
              </div>
            ) : (
              <span className="text-gray-500">Select your team</span>
            )}
          </div>
          
          {openDropdown === name && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
              {teams.length > 0 ? (
                teams.map((team) => (
                  <div
                    key={team._id}
                    onClick={() => handleTeamChange(name, team._id)}
                    className="flex items-center space-x-3 px-4 py-3 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                  >
                    <ProfileImage
                      image={team.logo}
                      alt={team.name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div className="flex-1">
                      <div className="font-medium">{team.name}</div>
                      <div className="text-sm text-gray-500">{team.uniqueName} • {team.type}</div>
                      <div className="text-xs text-gray-400">{team.sport} • {team.gender}</div>
                    </div>
                    <div className="text-xs text-gray-400">
                      {team.members?.length || 0} members
                    </div>
                  </div>
                ))
              ) : (
                <div className="px-4 py-3 text-gray-500 text-center">
                  No teams available
                </div>
              )}
            </div>
          )}
          {errors[name] && (
            <p className="mt-1 text-sm text-red-600">{errors[name]}</p>
          )}
        </div>
      );
    };
  }, [teams, openDropdown, errors, getSelectedTeam, handleTeamChange]);

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
            {/* Header */}
            <div className="px-6 py-6" style={{background: 'linear-gradient(to right, #3B82F6, #1D4ED8)'}}>
              <h2 className="text-2xl font-bold text-white text-center">
                Find Opponent Team
              </h2>
              <p className="text-blue-100 text-center mt-2">
                Search for the team you want to challenge
              </p>
            </div>

            <div className="p-8">
              {/* Search Form */}
              <div className="mb-8">
                <label
                  htmlFor="uniqueName"
                  className="block text-sm font-semibold text-gray-700 mb-3"
                >
                  <svg className="w-5 h-5 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  Team Unique Name
                </label>
                <div className="flex gap-3">
                  <input
                    type="text"
                    id="uniqueName"
                    value={uniqueName}
                    onChange={(e) => setUniqueName(e.target.value)}
                    className="flex-1 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-lg px-4 py-3 bg-gray-50 transition-all duration-200"
                    placeholder="Enter team unique name (e.g., @teamname)"
                    onKeyPress={(e) => e.key === 'Enter' && handleTeamSearch()}
                  />
                  <button
                    onClick={handleTeamSearch}
                    disabled={searchLoading}
                    className="inline-flex items-center px-6 py-3 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105 transition-all duration-200"
                  >
                    {searchLoading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Searching...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        Search
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Error Message */}
              {searchError && (
                <div className="rounded-lg bg-red-50 p-4 mb-6 border border-red-200">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">
                        {searchError}
                      </h3>
                    </div>
                  </div>
                </div>
              )}

              {/* Team Card */}
              {SearchedTeamCard}
            </div>
          </div>
        );

      case 2:
        return (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Competition Details</h2>
            
            <div className="space-y-6">
              {/* Selected Opponent Team Display */}
              {OpponentTeamDisplay}

              {/* Your Team Selection */}
              <div>
                <CustomTeamDropdown
                  name="senderTeamId"
                  value={formData.senderTeamId}
                  teams={teams}
                  label="Your Team"
                />
                {formData.senderTeamId && <TeamPreview teamId={formData.senderTeamId} />}
              </div>

              {/* Competition Details */}
              <div>
                {/* Title */}
                <div>
                  <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
                    Competition Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    placeholder="e.g. Championship Semi-Final"
                    className={`w-full px-3 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.title ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.title && (
                    <p className="mt-1 text-sm text-red-600">{errors.title}</p>
                  )}
                  <p className="mt-1 text-sm text-gray-500">{formData.title.length}/100 characters</p>
                </div>
              </div>

              {/* Description */}
              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={4}
                  placeholder="Describe the competition details, rules, and expectations..."
                  className={`w-full px-3 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.description ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.description && (
                  <p className="mt-1 text-sm text-red-600">{errors.description}</p>
                )}
                <p className="mt-1 text-sm text-gray-500">{formData.description.length}/1000 characters</p>
              </div>

              {/* Message */}
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  Additional Message <span className="text-gray-400">(Optional)</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={3}
                  placeholder="Add any additional information or special requests..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Schedule Details</h2>
            
            <div className="space-y-6">
              {/* Error Message */}
              {errors.submit && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600">{errors.submit}</p>
                </div>
              )}

              {/* Competition Summary */}
              {CompetitionSummary}

              {/* Date Selection */}
              <div className="max-w-md">
                {/* Start Date */}
                <div>
                  <label htmlFor="proposedStartDate" className="block text-sm font-medium text-gray-700 mb-2">
                    Competition Start Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="datetime-local"
                    id="proposedStartDate"
                    name="proposedStartDate"
                    value={scheduleData.proposedStartDate}
                    onChange={handleScheduleChange}
                    className={`w-full px-3 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.proposedStartDate ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.proposedStartDate && (
                    <p className="mt-1 text-sm text-red-600">{errors.proposedStartDate}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center space-x-4 mb-4">
          <button
            onClick={() => navigate('/organization/competition-requests')}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors duration-200"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Create Competition Request</h1>
            <p className="text-gray-600 mt-2">Send a competition request to another organization</p>
          </div>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-center">
          {[1, 2, 3].map((step) => (
            <React.Fragment key={step}>
              <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${
                currentStep >= step 
                  ? 'bg-blue-600 border-blue-600 text-white' 
                  : 'bg-white border-gray-300 text-gray-400'
              }`}>
                {currentStep > step ? (
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <span className="text-sm font-medium">{step}</span>
                )}
              </div>
              {step < totalSteps && (
                <div className={`w-16 h-1 mx-2 ${
                  currentStep > step ? 'bg-blue-600' : 'bg-gray-300'
                }`} />
              )}
            </React.Fragment>
          ))}
        </div>
        <div className="flex justify-center mt-2">
          <div className="text-sm text-gray-600">
            Step {currentStep} of {totalSteps}: {
              currentStep === 1 ? 'Find Opponent' :
              currentStep === 2 ? 'Competition Details' :
              'Schedule & Review'
            }
          </div>
        </div>
      </div>

      {/* Step Content */}
      <div className="mb-8">
        {renderStepContent()}
      </div>

      {/* Navigation Buttons */}
      <div className="flex items-center justify-between">
        <button
          onClick={handlePrevStep}
          disabled={currentStep === 1}
          className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Previous
        </button>

        <div className="flex space-x-3">
          <button
            onClick={() => navigate('/organization/competition-requests')}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>

          {currentStep < totalSteps ? (
            <button
              onClick={handleNextStep}
              disabled={currentStep === 1 && !searchedTeam}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
              <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="inline-flex items-center px-6 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Creating Request...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Create Competition Request
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateCompetitionRequest; 